import React from "react";

import { faArrowLeft } from "@fortawesome/sharp-regular-svg-icons/faArrowLeft";
import { faArrowRight } from "@fortawesome/sharp-regular-svg-icons/faArrowRight";
import { faTimes } from "@fortawesome/sharp-regular-svg-icons/faTimes";
import Dialog from "@radix-ui/react-dialog";
import clsx from "clsx";

import { themeBackgroundWhite } from "~styles/themes/theme.background_white.css";

import { Box } from "~components/Box";
import { IconFontAwesome } from "~components/IconFontAwesome";
import { Link } from "~components/Link";
import { Typography } from "~components/Typography";

import { type MobileFooter, type NavCategory } from "~sections/Nav/Nav.types";
import { ListItem } from "~sections/Nav/desktop_new/_ListItem";
import * as desktopNavStyles from "~sections/Nav/desktop_new/styles.css";
import { Callout } from "~sections/Nav/mobile_new/_Callout";
import { Footer } from "~sections/Nav/mobile_new/_Footer";
import * as styles from "~sections/Nav/mobile_new/styles.css";

interface CategoriesProps extends MobileFooter {
  navCategory: NavCategory;
  closeMainNav: () => void;
  mobileBackLabel: string;
  /* a11y */
  ariaLabelCloseMainNav: string;
  ariaLabelBackToMainNav: string;
}

export function Categories({
  buttonLink,
  secondaryButtonLink,
  secondaryButtonText,
  buttonText,
  navCategory,
  closeMainNav,
  mobileBackLabel,
  ariaLabelCloseMainNav,
  ariaLabelBackToMainNav,
}: CategoriesProps) {
  const {
    title,
    categories,
    link,
    extra: extraArray,
    callOut: callOutArray,
  } = navCategory;
  const extra = extraArray && extraArray[0];
  const callOut = callOutArray && callOutArray[0];
  if (categories && !link?.id) {
    return (
      <Dialog.Root>
        <Dialog.Trigger asChild className={styles.dialogTrigger}>
          <button type="button" aria-label={title} className={styles.category}>
            <Typography textAppearance="body_lg" fontWeight="semibold">
              {title}
            </Typography>
            <IconFontAwesome size="sm" icon={faArrowRight} />
          </button>
        </Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Overlay className={styles.dialogOverlay} />
          <Dialog.Content
            className={clsx(styles.dialogContent, themeBackgroundWhite)}
          >
            <Box className={styles.dialogHeader}>
              <Dialog.Close asChild>
                <button type="button" aria-label={ariaLabelBackToMainNav}>
                  <IconFontAwesome
                    size="sm"
                    icon={faArrowLeft}
                    marginRight="spacing1"
                  />
                  <Typography fontWeight="semibold">
                    {mobileBackLabel}
                  </Typography>
                </button>
              </Dialog.Close>
              <button
                type="button"
                aria-label={ariaLabelCloseMainNav}
                onClick={() => closeMainNav()}
              >
                <IconFontAwesome size="lg" icon={faTimes} />
              </button>
            </Box>
            <Box className={styles.dialogScrollableContent}>
              <Box className={styles.dialogScrollableContentCategories}>
                {categories?.map((category) => {
                  return (
                    <Box
                      className={clsx(
                        desktopNavStyles.navigationMenuCategoryWrapper,
                        styles.navigationMenuCategoryWrapper
                      )}
                      key={category.title}
                    >
                      {category.title && (
                        <Typography
                          textAppearance="body_md"
                          fontWeight="semibold"
                          className={
                            desktopNavStyles.navigationMenuCategoryHeading
                          }
                          aria-hidden
                        >
                          {category.title}
                        </Typography>
                      )}
                      <ul
                        className={clsx(
                          desktopNavStyles.navigationMenuCategory,
                          styles.navigationMenuCategory
                        )}
                      >
                        {category?.listItems?.map((listItem) => {
                          return (
                            <ListItem
                              title={listItem.title}
                              link={listItem.link}
                              icon={listItem.icon}
                              key={listItem.title}
                              subListItems={listItem?.subListItems}
                            >
                              {listItem?.description}
                            </ListItem>
                          );
                        })}
                      </ul>
                    </Box>
                  );
                })}
                {extra && (
                  <Box
                    className={clsx(
                      desktopNavStyles.navigationMenuCategoryWrapper,
                      desktopNavStyles.navigationMenuExtraWrapper
                    )}
                    key={extra.title}
                  >
                    {extra.title && (
                      <Typography
                        textAppearance="body_md"
                        fontWeight="semibold"
                        className={
                          desktopNavStyles.navigationMenuCategoryHeading
                        }
                        aria-hidden
                      >
                        {extra.title}
                      </Typography>
                    )}
                    <ul
                      className={clsx(
                        desktopNavStyles.navigationMenuExtra,
                        desktopNavStyles.navigationMenuCategory,
                        styles.navigationMenuCategory
                      )}
                    >
                      {extra.subListItems.map((subListItem) => {
                        return (
                          <ListItem
                            title={subListItem.title}
                            link={subListItem.link}
                            key={subListItem.title}
                          />
                        );
                      })}
                    </ul>
                  </Box>
                )}
              </Box>
              {callOut && <Callout {...callOut} />}
            </Box>
            <Box className={styles.dialogFooter}>
              <Footer
                buttonText={buttonText}
                buttonLink={buttonLink}
                secondaryButtonText={secondaryButtonText}
                secondaryButtonLink={secondaryButtonLink}
              />
            </Box>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }

  if (link && !categories) {
    return (
      <Link link={link} className={styles.category}>
        <Typography textAppearance="body_lg" fontWeight="semibold">
          {title}
        </Typography>
      </Link>
    );
  }

  return null;
}
